import { useState } from 'react';
import { Alert, Badge, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareNodes } from '@fortawesome/free-solid-svg-icons';

import useProductAllocation from '../../hooks/useProductAllocation';
import { fetchJobOrderContext } from '../../features/jobs/api/jobsApi';
import { cableLabel } from '../../helpers/cableLabel';
import ProductAllocationModal from './ProductAllocationModal';

/**
 * "Some orders want this" — offered where the product is, not where the orders are.
 *
 * A planner in the launch wizard is looking at one product with metres to
 * spare. Asking "who wants this?" meant leaving the wizard for the simulation
 * page, finding the product again in a table of everything on the floor, and
 * coming back. The question belongs beside the product it is about.
 *
 * Silent unless there is something to say: no free cable, or nobody waiting,
 * and nothing is drawn at all. A chip that is always there teaches people to
 * stop seeing it.
 */

const fmt = n => Math.round(Number(n) || 0).toLocaleString();

const ProductAllocationButton = ({ jobId, className = '', onAllocated }) => {
  const [open, setOpen] = useState(false);
  const { byJob, busy, note, error, allocate, release } = useProductAllocation({
    jobIds: null
  });

  const product = jobId ? byJob.get(String(jobId)) : null;
  if (!product) return null;

  const waiting = new Set(
    (product.candidates || [])
      .filter(c => (c.items || []).some(i => i.unitsOpen > 0))
      .map(c => c.orderCode)
  ).size;

  // Nothing free, nothing to offer. It used to show anyway whenever a promise
  // had been made, and then read the wrong half of its own condition: "1 order
  // wants this · 0 m free" — an invitation and its refusal in one sentence.
  //
  // Undoing a promise does not go missing with it: the Order Reservation panel
  // is on this same card, and it appears as soon as a promise exists.
  if (product.freeMeters <= 0 || !waiting) return null;

  const label = `${waiting} order${
    waiting === 1 ? ' wants' : 's want'
  } this · ${fmt(product.freeMeters)} m free`;

  return (
    <>
      <Badge
        bg="success"
        variant="phoenix"
        className={`fs-10 ${className}`}
        role="button"
        style={{ cursor: 'pointer' }}
        title="Send this product's cable to an order, or take a promise back"
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <FontAwesomeIcon icon={faShareNodes} className="me-1" />
        {label}
      </Badge>

      <Modal show={open} onHide={() => setOpen(false)} size="xl" centered scrollable>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fs-7">
            {cableLabel(product.product, product.productLabel || product.productKey)}
            <span className="text-body-tertiary fs-9 ms-2">{product.jobCode}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {error && (
            <Alert variant="danger" className="py-2 fs-9">
              {error}
            </Alert>
          )}
          {note && (
            <Alert variant="success" className="py-2 fs-9">
              {note}
            </Alert>
          )}
          {busy && !error && (
            <div className="d-flex align-items-center gap-2 mb-2 fs-9 text-700">
              <Spinner animation="border" size="sm" /> saving…
            </div>
          )}
          <ProductAllocationModal
            product={product}
            busy={busy}
            onAllocate={async picks => {
              const ok = await allocate(picks);
              if (!ok) return;
              // Hand the caller the freshly built context, so a wizard holding
              // this product in local state shows the new reservation at once
              // rather than after a reload.
              if (onAllocated) {
                try {
                  const ctx = await fetchJobOrderContext(jobId);
                  onAllocated(jobId, ctx?.orders || []);
                } catch {
                  // The write went through; a stale panel is worth less than a
                  // scary message about it.
                }
              }
              setOpen(false);
            }}
            onRelease={promise => release(product, promise)}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProductAllocationButton;
